import * as StaffDocumentAction from './staff-documents.actions';

import {createEntityAdapter, EntityAdapter} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import dayjs from 'dayjs';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {StaffDocument, StaffDocumentSearchParams, StaffDocumentType} from '@accounting/_common/models/staff-document.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const staffDocumentsFeatureKey = 'staffDocuments';

export function sortByDate(a: StaffDocument, b: StaffDocument) {
    if (a.date !== b.date) {
        return StoredComponentHelperService.sortArrayComparer(a, b, 'date');
    }

    return (a.id || '').localeCompare(b.id || '');
}

export const adapter: EntityAdapter<StaffDocument> = createEntityAdapter<StaffDocument>({
    sortComparer: sortByDate
});

export const typedListInitialState: EntityStateModel<StaffDocument, StaffDocumentSearchParams> = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export interface StaffDocumentsState {
    RECRUITMENT: EntityStateModel<StaffDocument, StaffDocumentSearchParams>,
    DISMISSAL: EntityStateModel<StaffDocument, StaffDocumentSearchParams>,
    STAFFING_CHANGE: EntityStateModel<StaffDocument, StaffDocumentSearchParams>,
    VACATION: EntityStateModel<StaffDocument, StaffDocumentSearchParams>,
    SICK_LEAVE: EntityStateModel<StaffDocument, StaffDocumentSearchParams>,
    CIVIL_CONTRACT: EntityStateModel<StaffDocument, StaffDocumentSearchParams>,
    CROSS: EntityStateModel<StaffDocument, StaffDocumentSearchParams>,
    ABSENTEEISM: EntityStateModel<StaffDocument, StaffDocumentSearchParams>,
    MOBILIZATION: EntityStateModel<StaffDocument, StaffDocumentSearchParams>
}

export const initialState: StaffDocumentsState = {
    RECRUITMENT: {...typedListInitialState},
    DISMISSAL: {...typedListInitialState},
    STAFFING_CHANGE: {...typedListInitialState},
    VACATION: {...typedListInitialState},
    SICK_LEAVE: {...typedListInitialState},
    CIVIL_CONTRACT: {...typedListInitialState},
    CROSS: {...typedListInitialState},
    ABSENTEEISM: {...typedListInitialState},
    MOBILIZATION: {...typedListInitialState}
};


export const staffDocumentsReducer = createReducer(
    initialState,

    on(StaffDocumentAction.CheckState, (state, action) => {
        if (action.payload.documentType == 'CROSS') {
            if (action.payload.close) {
                return {
                    ...state,
                    CROSS: initialState['CROSS']
                };
            }
            return {...state};
        }

        if (action.payload.modalView) {
            return ({
                ...state,
                [action.payload.documentType]: adapter.removeAll({
                    ...state[action.payload.documentType],
                    savedFilter: !action.payload.close ? state[action.payload.documentType].filter : undefined,
                    pagination: state[action.payload.documentType].pagination,
                    filter: action.payload.close ? state[action.payload.documentType].savedFilter : undefined
                })
            });
        } else {
            if (action.payload.close) {
                return ({
                    ...state,
                    [action.payload.documentType]: adapter.setAll(action.payload.items, {
                        ...state[action.payload.documentType],
                        pagination: {...state[action.payload.documentType].pagination, number: 0},
                        componentOpened: false
                    })
                });
            }

            return {...state, [action.payload.documentType]: {...state[action.payload.documentType], componentOpened: true}};
        }
    }),

    on(StaffDocumentAction.LoadList, (state, action) => {
        return ({
            ...state,
            [action.payload.documentType]: {
                ...state[action.payload.documentType],
                listLoading: StoredComponentHelperService.updateListLoading(action.payload, state[action.payload.documentType].ids)
            }
        });
    }),

    on(StaffDocumentAction.LoadListSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.documentType]: action.payload.clearState
                ? adapter.setAll(action.payload.items, {
                    ...state[action.payload.documentType],
                    pagination: action.payload.pagination,
                    listLoading: undefined

                })
                : adapter.upsertMany(action.payload.items, {
                    ...state[action.payload.documentType],
                    pagination: action.payload.pagination,
                    listLoading: undefined
                }),
        });
    }),


    on(StaffDocumentAction.LoadListFailure, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            listLoading: undefined
        }
    })),


    on(StaffDocumentAction.SetPagination, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            pagination: {
                ...state[action.payload.documentType].pagination,
                number: action.payload.next ? StoredComponentHelperService.setNextPage(state[action.payload.documentType].pagination, state[action.payload.documentType].ids?.length) : 0
            }
        }
    })),


    on(StaffDocumentAction.SetFilter, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            filter: action.payload.filter
        }
    })),

    on(StaffDocumentAction.SetLoading, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            loading: action.payload.loading
        }
    })),

    on(StaffDocumentAction.SetItemsLoading, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.id, action.payload.isStart)
        }
    })),

    on(StaffDocumentAction.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state[action.payload.documentType]?.filter, action.payload.item);
        if (savedItemAvailableByFilter) {
            return {
                ...state,
                [action.payload.documentType]: adapter.setOne(action.payload.item, {
                    ...state[action.payload.documentType],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, false),
                    pagination: {
                        ...state[action.payload.documentType].pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.documentType].pagination, state[action.payload.documentType]?.ids)
                    }
                })
            };
        } else {
            return ({
                ...state,
                [action.payload.documentType]: adapter.removeOne(action.payload.item.id!, {
                    ...state[action.payload.documentType],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, false),
                    pagination: {
                        ...state[action.payload.documentType].pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.documentType].pagination, state[action.payload.documentType]?.ids, true)
                    }
                })
            });
        }
    }),

    on(StaffDocumentAction.DeleteItem, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, true)
        }
    })),

    on(StaffDocumentAction.DeleteItemSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.documentType]: adapter.removeOne(action.payload.item.id!, {
                ...state[action.payload.documentType],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, false),
                pagination: {
                    ...state[action.payload.documentType].pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.documentType].pagination, state[action.payload.documentType]?.ids, true)
                }
            })
        });
    }),

    on(StaffDocumentAction.DeleteItemFailure, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, false)
        }
    })),

    on(StaffDocumentAction.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const selectAllEntities = (type: StaffDocumentType | 'CROSS') => adapter.getSelectors((state: StaffDocumentsState) => state[type]).selectAll;

const isAvailableByFilter = (searchParams: StaffDocumentSearchParams | undefined, item: StaffDocument): boolean => {
    if (!searchParams) {
        return true;
    }

    if (searchParams.documentType && item.type != searchParams.documentType) {
        return false;
    }

    if (searchParams.from && dayjs(searchParams.from).isAfter(dayjs(item.date))) {
        return false;
    }

    if (searchParams.to && dayjs(item.date).isAfter(dayjs(searchParams.to))) {
        return false;
    }

    return !((searchParams.operative || searchParams.operative === false) && searchParams.operative !== item.operative);
};
